import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html } from "lit";
import { property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { msg } from "@lit/localize";
//#region src/pd-pricetable/PdPricetable.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Price table component displaying articles with individual prices and tax calculations.
*
* @tagname pd-pricetable
* @summary Table displaying articles with prices, tax calculation, and totals.
*
*/
var PdPricetable = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.priceData = {
			articles: [],
			tax: 0
		};
		this._totalPrice = 0;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
        max-width: 100%;
        --table-spacing: 1rem;
        --table-radius: 1rem;

        --table-bg: var(--game-color-dark, #fefefe);

        --table-header-color: var(--game-color-light, #fefefe);
        --table-header-bg: var(--game-color-dark, #177e89);

        --table-row-odd-bg: var(--game-color-light, #fefefe);

        --table-small-cell-bg: var(--game-color-light, lightgrey);
      }

      .table {
        position: relative;
        z-index: 1;
        width: 100%;
        background: var(--table-bg);
      }

      .table_expanded {
        border-bottom: 2px solid var(--table-small-cell-bg);
      }

      .table_expanded .table__header .table__cell {
        position: relative;
        z-index: 10;
        background-color: var(--table-header-bg);
        box-shadow: 0 2px 0 var(--table-small-cell-bg);
        font-weight: bold;
        color: var(--table-header-color);
        font-family: Oswald;
      }

      .table_expanded .table__body .table__row:nth-child(odd) {
        background-color: var(--table-row-odd-bg);
      }

      .table_expanded .table__cell {
        padding: calc(var(--table-spacing) / 2);
      }

      .text-left {
        text-align: left;
      }

      .text-center {
        text-align: center;
      }

      .text-right {
        text-align: right;
      }

      .bold {
        font-size: 1.2em;
        font-family: Oswald;
        color: #084c61;
      }

      .light {
        color: grey;
      }

      .border-row {
        border-bottom: 1px solid black;
      }
    `];
	}
	render() {
		return html`
      <table class="${classMap({
			table: true,
			table_expanded: true
		})}">
        <thead class="table__header">
          <tr class="table__row">
            <th class="table__cell" scope="col">
              ${msg("Artikel", { id: "pd.price.table.article" })}
            </th>
            <th class="table__cell" scope="col">
              ${msg("Preis", { id: "pd.price.table.price" })}
            </th>
          </tr>
        </thead>
        <tbody class="table__body">
          ${this._renderBody()}
        </tbody>
        <tfoot>
          <tr>
            <td class="table__cell text-right bold" role="cell">
              ${msg("Gesamt:", { id: "pd.price.table.total" })}
            </td>
            <td class="table__cell text-right bold" role="cell">
              ${this._formatPrice(this._totalPrice * (1 + this.priceData.tax))}
              €
            </td>
          </tr>
        </tfoot>
      </table>
      <p>* ${this._getTaxInfo()}</p>
    `;
	}
	_renderBody() {
		this._totalPrice = 0;
		return html`
      ${this.priceData.articles.map((article) => {
			const price = article.price ? Number(article.price) : 0;
			this._totalPrice += price;
			return html`
          <tr class="table__row">
            <td class="table__cell text-left" role="cell">${article.name}</td>
            <td class="table__cell text-right" role="cell">
              ${article.price ? this._formatPrice(price) : "--"} €
            </td>
          </tr>
        `;
		})}
      <tr class="table__row">
        <td class="table__cell text-right border-row light" role="cell">
          ${this.priceData.tax * 100}% BTW*
        </td>
        <td class="table__cell text-right border-row light" role="cell">
          ${this._formatPrice(this._totalPrice * this.priceData.tax)} €
        </td>
      </tr>
    `;
	}
	_formatPrice(val) {
		return val.toFixed(2);
	}
	_getTaxInfo() {
		switch (this.priceData.tax) {
			case 0: return msg("Ohne Umsatzsteuer", { id: "pd.price.table.percent.noTax" });
			case .06: return msg("6% Umsatzsteuer für Wohngebäude, die älter als 10 Jahre sind.", { id: "pd.price.table.percent.6" });
			case .21: return msg("21% Umsatzsteuer für Wohngebäude, die jünger als 10 Jahre sind.", { id: "pd.price.table.percent.21" });
			default: return "--";
		}
	}
};
__decorate([property({ type: Object })], PdPricetable.prototype, "priceData", void 0);
//#endregion
export { PdPricetable };
